import * as Constants from "utils/Constants";
import * as actionConstants from "../constants/action-types";

import authService from '../../components/api-authorization/AuthorizeService';
import axios from 'axios'
import toast from 'utils/toast';

function errorResponseHandler(error) {

}

// A 403 bounces the user home. Home is now HomeResolver, which sends a user holding
// exactly one tool claim straight back to that tool (Branch 3) — so if the tool itself is
// what 403'd, the bounce lands them right back on the failing page and round it goes.
// Each hop is a full document load, so a module-level flag would reset every time; the
// marker has to outlive the navigation, hence sessionStorage. One bounce per window is
// what we want anyway: if the user is still being refused after being sent home once,
// sending them home again is not going to help, and the toast has already told them why.
const HOME_BOUNCE_KEY = 'as360.403HomeBounceAt';
const HOME_BOUNCE_WINDOW_MS = 30000;

// Home is now a real destination users sit on, not just somewhere we send them: `/` is the
// Custom Dashboard for anyone with a reporting claim. Bouncing a 403 raised ON that page
// back to itself is a full document reload that fixes nothing and discards page state.
// Worse, DashboardPage registers a beforeunload handler while the dashboard is in edit
// mode, so the pointless reload prompts "leave site?" and can cost the user an unsaved
// layout. /HomePage resolves to the same place, so treat both as already-home.
function isAlreadyHome() {
    try {
        const path = window.parent.location.pathname;
        return path === '/' || /^\/homepage\/?$/i.test(path);
    } catch (e) {
        // Cross-origin parent: we cannot tell, so allow the bounce rather than block it.
        return false;
    }
}

function recentlyBouncedHome() {
    try {
        const at = Number(window.sessionStorage.getItem(HOME_BOUNCE_KEY));
        return Number.isFinite(at) && at > 0 && Date.now() - at < HOME_BOUNCE_WINDOW_MS;
    } catch (e) {
        // Storage can throw outright (Safari private mode, blocked site data). Treat an
        // unreadable marker as "no recent bounce" so the redirect still works normally.
        return false;
    }
}

function markBouncedHome() {
    try {
        window.sessionStorage.setItem(HOME_BOUNCE_KEY, String(Date.now()));
    } catch (e) {
        // Non-fatal: without the marker we simply lose loop protection.
    }
}


// Request interceptor for API calls
axios.interceptors.request.use(
    async config => {

        const token = await authService.getAccessToken();
        config.headers.Authorization =  !token ? {} : `Bearer ${token}`;
   
        return config;
    },
    error => {
        return Promise.reject(error)
  });


// Add a response interceptor
axios.interceptors.response.use(function (response) {

    const suppressToast = response.config.headers['X-Suppress-Toast'] === 'true' || 
                         response.config.suppressToast === true;
    
    if (!response.config.url.toUpperCase().includes("UPLOAD") && !suppressToast) {

        const messages = response.data.Messages? response.data.Messages : response.data.messages;
        const operationSuccessful = response.data.OperationSuccessful? response.data.OperationSuccessful : response.data.operationSuccessful;
        
        if (operationSuccessful) {
            if (response.config.method !== "get") {
                if (messages.length > 0) {
                    for (let index in messages) {
                        let msg = messages[index];
                        const messageContent = msg.MessageContent ? msg.MessageContent : msg.messageContent;
                        const messageTypeId = msg.MessageTypeId ? msg.MessageTypeId : msg.messageTypeId;

                        if (messageTypeId === actionConstants.messageTypeEnum.Warning) {
                            toast.warning(messageContent);
                        }
                        else if (messageTypeId === actionConstants.messageTypeEnum.Success) {
                            toast.success(messageContent);
                        }
                    }
                } 
                else {
                    const includeKeys = [
                        "PROVIDEREXPORTVIEW",
                        "SENDVERIFYEMAIL",
                        "SENDCOMMENTNOTIFICTION",
                        "SendCommentNotifiction"
                    ];
                    const url = response.config.url.toUpperCase();
                    const matches = includeKeys.some(key => url.includes(key));
                    if(!matches){
                        toast.success("Operation Successful.");
                    }
                }
            }
        } 
        else {
            for (let index in messages) {
                let msg = messages[index];
                const messageContent = msg.MessageContent ? msg.MessageContent : msg.messageContent;
                const messageTypeId = msg.MessageTypeId ? msg.MessageTypeId : msg.messageTypeId;
                if (messageTypeId === actionConstants.messageTypeEnum.Warning) {
                    toast.warning(messageContent);
                }
              
                else if (messageTypeId === actionConstants.messageTypeEnum.Error) {
                    toast.error(messageContent);
                }
                else if (messageTypeId === actionConstants.messageTypeEnum.RequestOutOfDate) {
                    document.getElementById('old-version-warning-modal').style.display = "block";
                }
            }
        }
    }
    response.data = response.data || [];

    return response;
}, 
function (error) {    
    console.log(error);

    // A request can opt out of the generic error toast (best-effort fetches that render
    // their own inline failure state) via config.suppressToast or the X-Suppress-Toast header.
    const cfg = error.config || {};
    const suppressToast = cfg.suppressToast === true || cfg.headers?.['X-Suppress-Toast'] === 'true';

    // No response = transport-level failure (offline / timeout / CORS / cancelled request).
    // Guard so we never read .status off an undefined response (which threw a raw
    // "Cannot read properties of undefined (reading 'status')" into the UI), and give the
    // user an actionable message instead of the generic catch-all.
    const status = error.response?.status;

    if (status === 401) {
        window.parent.location.href = "/authentication/logout";
    }
    else if (status === 403) {
        toast.error('You are not allowed to perform this action. Please contact the system administrator.');
        if(!window.parent.location.href.toLowerCase().includes(Constants.Route.DatabaseOverview) && !isAlreadyHome() && !recentlyBouncedHome()) {
            setTimeout(function () {
                // "/" not "/HomePage": HomeResolver decides where home is for this user
                // (task 74894). Sending them to the legacy page was the reported symptom —
                // an error dropped users onto the old home page and stranded them there.
                // Marked here rather than when the bounce was scheduled. The marker exists
                // to stop a bounce LOOP, so it should record bounces that actually happen;
                // set 2s earlier, a user who navigated away inside the window burned their
                // one bounce on a navigation that never occurred and lost the next 30s of
                // legitimate bounces.
                markBouncedHome();
                window.parent.location.href = "/";
            }, 2000);
        }
    }
    else if (!error.response) {
        // Transport-level failure: no HTTP response ever came back.
        if (!suppressToast) {
            toast.error('Could not reach the server. Check your connection and try again.');
        }
    }
    else if (!suppressToast)
    {
        toast.error('An unexpected error has occurred. Please contact the system administrator.');
    }
    
    return Promise.reject(error);
});

export default errorResponseHandler;